import { formatCurrency } from '@/lib/currency-utils'
import { formatFullDate } from '@/lib/date-utils'
import {
  BillingIntervalEnum,
  PlanKeyEnum,
  type OrganizationBillingState,
  type PlanOption,
} from '@/gen/cortado/v1/subscriptions_pb'

export type CheckoutSelection = {
  planKey: PlanKeyEnum
  billingInterval: BillingIntervalEnum
  option: PlanOption
}

export const PLAN_LABELS: Record<PlanKeyEnum, string> = {
  [PlanKeyEnum.PLAN_KEY_UNSPECIFIED]: 'Plan',
  [PlanKeyEnum.PLAN_KEY_FOUNDER]: 'Founder',
  [PlanKeyEnum.PLAN_KEY_CCC]: 'Coffee Cart Collective',
  [PlanKeyEnum.PLAN_KEY_STANDARD]: 'Standard',
}

export const INTERVAL_LABELS: Record<BillingIntervalEnum, string> = {
  [BillingIntervalEnum.BILLING_INTERVAL_UNSPECIFIED]: 'Interval',
  [BillingIntervalEnum.BILLING_INTERVAL_MONTHLY]: 'Monthly',
  [BillingIntervalEnum.BILLING_INTERVAL_SEMIANNUAL]: '6 months',
  [BillingIntervalEnum.BILLING_INTERVAL_ANNUAL]: 'Annual',
}

/**
 * The single client-side calculation this flow is allowed to make: the
 * per-month equivalent of a term price. Every other figure is displayed from
 * the server-quoted fields verbatim.
 */
export function perMonthEquivalentCents(option: PlanOption): number {
  return option.intervalMonths > 0 ? Number(option.priceCents) / option.intervalMonths : 0
}

export interface OrderSummaryLine {
  label: string
  value: string
}

export interface OrderSummary {
  planLabel: string
  intervalLabel: string
  termPriceFormatted: string
  perMonthEquivalentLabel: string | null
  lines: OrderSummaryLine[]
  breakdown: string | null
  trialLabel: string
  trialAvailable: boolean
  // Caveat under the term price. Null once Stripe has quoted a real tax line.
  taxNote: string | null
  dueTodayFormatted: string
  firstChargeAmountLabel: string
  firstChargeDateLabel: string
}

/**
 * Totals published by the live Checkout Session once the customer's billing
 * address makes automatic tax computable. Every amount is Stripe's own
 * preformatted display string, carried through verbatim - this flow never does
 * client-side money arithmetic.
 */
export interface LiveCheckoutTotals {
  dueToday: string
  subtotal: string
  taxAmount: string
  taxReady: boolean
  discounts: Array<{ displayName: string; amount: string }>
  recurringTotal: string | null
  // Unix seconds, in the shape formatFullDate accepts.
  trialEndsAt: number | null
}

/** The subset of the Stripe checkout value the order summary reads. */
export interface LiveCheckoutSource {
  total: {
    subtotal: { amount: string }
    taxExclusive: { amount: string }
    total: { amount: string }
  }
  tax: { status: string }
  discountAmounts: Array<{ displayName: string; amount: string }> | null
  recurring: {
    dueNext: { total: { amount: string } }
    trial: { trialEnd: number } | null
  } | null
}

const TAX_PENDING_NOTE = 'Calculated after address'
const TAX_DEFAULT_NOTE = 'plus applicable tax'

export function mapLiveCheckoutTotals(checkout: LiveCheckoutSource): LiveCheckoutTotals {
  return {
    dueToday: checkout.total.total.amount,
    subtotal: checkout.total.subtotal.amount,
    taxAmount: checkout.total.taxExclusive.amount,
    taxReady: checkout.tax.status === 'ready',
    discounts: (checkout.discountAmounts ?? []).map((discount) => ({
      displayName: discount.displayName,
      amount: discount.amount,
    })),
    recurringTotal: checkout.recurring?.dueNext.total.amount ?? null,
    trialEndsAt: checkout.recurring?.trial?.trialEnd ?? null,
  }
}

/**
 * Overlays the live session totals onto the quoted summary. Stripe's strings
 * replace the quote wherever it has spoken; nothing here is recomputed.
 */
export function applyLiveCheckoutTotals(
  summary: OrderSummary,
  live: LiveCheckoutTotals | null
): OrderSummary {
  if (!live) return summary

  const lines = summary.lines.filter((line) => line.label !== 'Discount')
  for (const discount of live.discounts) {
    lines.push({ label: discount.displayName, value: `-${discount.amount}` })
  }
  if (live.taxReady) {
    lines.push({ label: 'Tax', value: live.taxAmount })
  }

  const trialing = live.trialEndsAt !== null
  return {
    ...summary,
    lines,
    taxNote: live.taxReady ? null : TAX_PENDING_NOTE,
    dueTodayFormatted: live.dueToday,
    firstChargeAmountLabel:
      trialing && live.recurringTotal ? live.recurringTotal : summary.firstChargeAmountLabel,
    firstChargeDateLabel:
      trialing && live.trialEndsAt
        ? formatFullDate({ seconds: live.trialEndsAt })
        : summary.firstChargeDateLabel,
  }
}

const GENERIC_AMOUNT = 'the amount shown in checkout'
const GENERIC_DATE = 'the date shown in checkout'

/**
 * Structured order-summary rows for a chosen plan option. Replaces the old
 * single-line formatStartTrialTerms string. Amounts and dates come from the
 * server quote on the selected PlanOption / billing state - nothing here
 * recomputes a price.
 */
export function buildOrderSummary(
  billingState: OrganizationBillingState,
  selection: CheckoutSelection
): OrderSummary {
  const { option } = selection
  const trialAvailable = billingState.trialAvailable

  const amount = option.quotedFirstChargeFormatted.trim()
  const breakdown = option.quotedFirstChargeBreakdown.trim()
  const firstChargeAmountLabel = amount || GENERIC_AMOUNT

  const chargeAt = trialAvailable
    ? billingState.firstChargeAt ?? billingState.provisionalFirstChargeAt
    : billingState.provisionalFirstChargeAt
  const firstChargeDateLabel = chargeAt ? formatFullDate(chargeAt) : GENERIC_DATE

  const trialLabel = trialAvailable
    ? billingState.effectiveTrialDays > 0
      ? `${billingState.effectiveTrialDays}-day free trial`
      : 'Free trial'
    : 'Subscription starts today'

  const lines: OrderSummaryLine[] = []
  if (option.quotedBillableSeats > 0) {
    lines.push({
      label: 'Billable seats',
      value: String(option.quotedBillableSeats),
    })
  }
  if (option.quotedSeatChargeCents > 0n) {
    lines.push({
      label: 'Additional seats',
      value: `$${formatCurrency(Number(option.quotedSeatChargeCents))}`,
    })
  }
  if (option.quotedDiscountCents > 0n) {
    lines.push({
      label: 'Discount',
      value: `-$${formatCurrency(Number(option.quotedDiscountCents))}`,
    })
  }

  const perMonthCents = perMonthEquivalentCents(option)

  return {
    planLabel: PLAN_LABELS[selection.planKey],
    intervalLabel: INTERVAL_LABELS[selection.billingInterval],
    termPriceFormatted: option.priceFormatted,
    perMonthEquivalentLabel: perMonthCents > 0 ? `$${formatCurrency(perMonthCents)}/mo equivalent` : null,
    lines,
    breakdown: breakdown || null,
    trialLabel,
    trialAvailable,
    taxNote: TAX_DEFAULT_NOTE,
    dueTodayFormatted: trialAvailable ? '$0.00' : firstChargeAmountLabel,
    firstChargeAmountLabel,
    firstChargeDateLabel,
  }
}
